import cx from 'classnames';
import FlowTitleSEO from '../../ui/v2/flow-title';
import PageTitleSEO from '../../ui/v2/typography/page-title';
import Trans from 'next-translate/Trans';
import BPImage from '../../ui/v2/image';
import TabContainer from '../../ui/tabs/new-index';
import { ReactNode, useEffect, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { atom, useAtom } from 'jotai';

export const currentSelectionAtom = atom(undefined);

export const getInheritContent = (
  t,
  isVisible,
  currentSelection,
  setCurrentSelection,
  isSold,
  isReservated,
  inCart
) => {
  const linkContentClass =
    'flex flex-nowrap shrink-0 text-base px-4 py-[10px] text-neutral-80 h-fit min-w-fit';

  const backgroundDefaultConfig = `font-normal border-b-4 hover:border-b-4 hover:border-b-neutral-80 ${isSold
    ? 'bg-red-10 border-b-red-10 '
    : isReservated
      ? 'bg-yellow-10 border-b-yellow-10 '
      : inCart
        ? 'bg-green-10 border-b-green-10'
        : 'bg-neutral-5 border-b-neutral-5 '
    }`;
  return [
    {
      key: 'pp_details',
      tab: (
        <Link
          href={'#pp_details'}
          className={cx(
            linkContentClass,
            currentSelection === 'pp_details'
              ? 'font-bold bg-white border-b-4 border-b-bparts-100'
              : backgroundDefaultConfig
          )}
          onClick={() => {
            setCurrentSelection('pp_details');
          }}
        >
          {t('product:part_details_short')}
        </Link>
      ),
    },
    {
      key: 'pp_observations',
      tab: (
        <Link
          href={'#pp_observations'}
          className={cx(
            linkContentClass,
            currentSelection === 'pp_observations'
              ? 'font-bold bg-white border-b-4 border-b-bparts-100'
              : backgroundDefaultConfig
          )}
          onClick={() => {
            setCurrentSelection('pp_observations');
          }}
        >
          {t('product:observations')}
        </Link>
      ),
    },
    {
      key: 'pp_tec_info',
      tab: (
        <Link
          href={'#pp_tec_info'}
          className={cx(
            linkContentClass,
            currentSelection === 'pp_tec_info'
              ? 'font-bold bg-white border-b-4 border-b-bparts-100'
              : backgroundDefaultConfig
          )}
          onClick={() => {
            setCurrentSelection('pp_tec_info');
          }}
        >
          {t('product:tec_info')}
        </Link>
      ),
    },
    {
      key: 'pp_more_info',
      tab: (
        <Link
          href={'#pp_more_info'}
          className={cx(
            linkContentClass,
            currentSelection === 'pp_more_info'
              ? 'font-bold bg-white border-b-4 border-b-bparts-100'
              : backgroundDefaultConfig
          )}
          onClick={() => {
            setCurrentSelection('pp_more_info');
          }}
        >
          {t('product:more_info')}
        </Link>
      ),
    },
    isVisible.vehicle && {
      key: 'pp_see_vehicle',
      tab: (
        <Link
          href={'#pp_see_vehicle'}
          className={cx(
            linkContentClass,
            currentSelection === 'pp_see_vehicle'
              ? 'font-bold bg-white border-b-4 border-b-bparts-100'
              : backgroundDefaultConfig
          )}
          onClick={() => {
            setCurrentSelection('pp_see_vehicle');
          }}
        >
          {t('product:see_vehicle')}
        </Link>
      ),
    },
  ];
};

type ProductTitleProps = {
  productDetail: TProductDetail | undefined;
  isToShowCarImage?: boolean;
  children?: ReactNode;
  isSold?: boolean;
  isSticky?: boolean;
  isImageVisible?: boolean;
  isReservated?: boolean;
};

const ProductTitle = ({
  productDetail,
  isToShowCarImage,
  children,
  isSold,
  isSticky = false,
  isImageVisible,
  isReservated,
}: ProductTitleProps) => {
  const { t } = useTranslation();
  const [currentSelection, setCurrentSelection] = useAtom(currentSelectionAtom);
  const isVisible = {
    vehicle: productDetail?.unique_vehicle_slug,
    tech_info: productDetail?.technical_info,
  };
  const items = getInheritContent(
    t,
    isVisible,
    currentSelection,
    setCurrentSelection,
    isSold,
    isReservated,
    productDetail?.in_cart
  );

  useEffect(() => {
    if (window.location.hash) {
      setCurrentSelection(window.location.hash.replace('#', ''));
    }
  }, []);

  return (
    <FlowTitleSEO
      orientation="flex-col lg:flex-row !items-end"
      flowTitleClassName={cx('!pt-0 lg:pb-0 max-lg:pb-4 !pr-0')}
      flowSectionClassName={cx(
        '!px-0 grid gap-4 !items-start w-full lg:grid-cols-[6fr_2fr]'
      )}
      isSold={isSold}
      isReservated={isReservated}
      inCart={productDetail?.in_cart}
      fullWidth={true}
      cols={[
        <div
          key="leading"
          className="flex flex-col gap-4 items-start w-full lg:w-full"
        >
          <div className="flex flex-row gap-4 lg:gap-4 items-start">
            <div
              className={cx(
                isImageVisible ? 'lg:hidden' : '',
                'relative',
                'aspect-video',
                'shrink-0',
                'flex h-[48px] w-[48px]',
                isSold
                  ? 'bg-red-10'
                  : isReservated
                    ? 'bg-yellow-10'
                    : productDetail?.in_cart
                      ? 'bg-green-10'
                      : 'bg-neutral-5'
              )}
            >
              <BPImage
                src={
                  isToShowCarImage &&
                    productDetail?.images &&
                    productDetail.images.length > 0
                    ? productDetail?.images[0]
                    : productDetail?.car.brand_alias
                      ? `${process.env.STATIC_PREFIX}/brands/logos/${productDetail?.car.brand_alias}.svg`
                      : `${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`
                }
                layout="fill"
                objectFit="contain"
                objectPosition="center"
                fallbackSrc={`${process.env.PUBLIC_PREFIX}/img/car_placeholder.jpeg`}
                className={cx('bg-cover mix-blend-multiply')}
                alt={`${productDetail?.car.brand_alias || 'car placeholder'
                  } logo`}
              />
            </div>
            <div className="flex flex-col gap-2 items-start">
              <PageTitleSEO
                isSticky={isSticky}
                key={'title'}
                textAlign="left"
                titleFontClass="font-extrabold text-base lg:text-lg"
                subtitleFontClass={cx(
                  'font-bold text-base lg:text-lg',
                  'line-clamp-2'
                )}
                contentAlign="flex-row gap-1"
                title={[productDetail?.part.name].join(' ').replaceAll('|', '')}
                subtitle={[
                  ' ',
                  productDetail?.car.brand_name,
                  productDetail?.car.model_name,
                  productDetail?.car.version_name || '',
                  productDetail?.reference,
                ]
                  .join(' ')
                  .replaceAll('|', '')}
              />
              <h2
                key="stock"
                id="h2_sku"
                className="max-w-[780px] text-center text-base font-extrabold"
              >
                <span className={cx('inline-block')}>{productDetail?.sku}</span>
              </h2>
            </div>
          </div>
          <div className="!hidden w-full max-lg:hidden">
            <div className="flex flew-row h-fit max-lg:gap-4 overflow-y-auto">
              {items.map((t, index) => {
                return <div key={t?.key}>{t?.tab}</div>;
              })}
            </div>
          </div>
        </div>,
      ]}
      extraContent={[
        <div
          key="atc"
          className="flex flex-col gap-2 w-full justify-end max-lg:hidden h-full"
        >
          {children}
        </div>,
      ]}
    />
  );
};

export default ProductTitle;
